import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

/**
 * Stimulus controller for OpenAPI sidebar search.
 * Handles debounced search with minimum length validation and automatic reset.
 */
export default class SidebarSearchController extends Controller {
  static targets = ['input', 'results', 'clearButton'];
  static values = {
    delay: { type: Number, default: 200 },
    minLength: { type: Number, default: 2 },
    resetUrl: { type: String, default: '' },
  };

  declare readonly inputTarget: HTMLInputElement;
  declare readonly resultsTarget: HTMLElement & { src?: string };
  declare readonly clearButtonTarget: HTMLElement;
  declare readonly hasResultsTarget: boolean;
  declare readonly hasClearButtonTarget: boolean;
  declare readonly delayValue: number;
  declare readonly minLengthValue: number;
  declare readonly resetUrlValue: string;

  private timeout: number | null = null;

  connect() {
    this.updateClearButtonVisibility();
  }

  input() {
    this.updateClearButtonVisibility();

    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }

    this.timeout = window.setTimeout(() => {
      const value = this.inputTarget.value.trim();

      if (value.length < this.minLengthValue) {
        this.reset();
        return;
      }

      const form = this.inputTarget.closest('form');
      if (form) {
        form.requestSubmit();
      }
    }, this.delayValue);
  }

  clear() {
    this.inputTarget.value = '';
    this.updateClearButtonVisibility();
    this.inputTarget.focus();
    this.reset();
  }

  private updateClearButtonVisibility() {
    if (!this.hasClearButtonTarget) return;

    if (this.inputTarget.value.trim().length > 0) {
      this.clearButtonTarget.classList.remove('hidden');
    } else {
      this.clearButtonTarget.classList.add('hidden');
    }
  }

  private reset() {
    if (!this.resetUrlValue || !this.hasResultsTarget) {
      return;
    }

    if ('src' in this.resultsTarget) {
      this.resultsTarget.src = this.resetUrlValue;
    }
  }
}

application.register('openapi-ui--sidebar--search', SidebarSearchController);
